import React from 'react';
import { useNavigate } from 'react-router-dom';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import Disclaimer from './Disclaimer';
import BrandWordmark from './BrandWordmark';
import PoweredByTrAingle from './PoweredByTrAingle';
import PublicSiteFooter from './PublicSiteFooter';
import { LEGAL_REVIEW_DATE } from './legalDates';
import './InfoPages.css';

const SECURITY_HIGHLIGHTS = [
  {
    title: 'Audit trail',
    detail: 'Uploads, redactions, and downloads are tracked so the workflow stays reviewable.',
  },
  {
    title: 'Reality check',
    detail: 'The page separates what is already live from what is still being built or planned.',
  },
  {
    title: 'Responsible use',
    detail: 'Final verification remains required before any file leaves the organization.',
  },
];

function Security({ theme = 'dark', toggleTheme }) {
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  return (
    <div className={`info-page-shell ${isDark ? 'info-page-dark' : 'info-page-light'}`}>
      <a className="skip-link" href="#security-content">Skip to content</a>
      <div className="info-page-card">
        <div className="info-page-topbar info-page-topbar-sticky">
          <div className="info-page-brand"><BrandWordmark className="info-page-brand-mark" /><PoweredByTrAingle className="info-page-powered-by" /></div>
          <div className="info-page-topbar-actions">
            {toggleTheme ? (
              <button
                className="theme-toggle-btn info-page-theme-toggle"
                type="button"
                onClick={toggleTheme}
                aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
              >
                {isDark ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
              </button>
            ) : null}
            <button className="nav-primary-btn" type="button" onClick={() => navigate('/')}>
              Back to Home
            </button>
          </div>
        </div>

        <header className="info-page-header">
          <div className="info-page-badge">Security</div>
          <h1>Security Transparency</h1>
          <Disclaimer>
            <strong>Beta / Free Tier Disclaimer:</strong> Redacto is currently in a FREE launch / beta phase. Billing and paid subscription features are disabled at present. This launch does not obligate users to any charges.
          </Disclaimer>
          <p>What Redacto does today, what it does not claim, and where the product is still maturing.</p>
          <p className="info-page-updated">Last reviewed: {LEGAL_REVIEW_DATE}</p>
          <div className="info-page-breadcrumb">
            <button type="button" onClick={() => navigate('/')}>Home</button>
            <span>/</span>
            <span>Security</span>
          </div>
        </header>

        <div id="security-content" className="info-page-body info-page-body-readable info-page-legal-body">
          <div className="info-page-lead-card">
            <div className="info-page-section-kicker">What we do</div>
            <h2>Current controls and product behavior</h2>
            <p>
              This page describes the security-related controls Redacto actually uses today. It is intentionally plain,
              current, and limited to what is backed by the codebase and product behavior.
            </p>
          </div>

          <div className="info-page-grid info-page-highlight-grid" aria-label="Security highlights">
            {SECURITY_HIGHLIGHTS.map((card) => (
              <article key={card.title} className="info-page-panel info-page-highlight-panel">
                <h3>{card.title}</h3>
                <p>{card.detail}</p>
              </article>
            ))}
          </div>

          <section className="info-page-section info-page-legal-section">
            <h3>What we do</h3>
            <ul>
              <li>Audit logging — uploads, redactions, and downloads are logged with user and timestamp.</li>
              <li>Human review before export — users review final output before release or external use.</li>
              <li>TLS in production — documents are intended to be transmitted over HTTPS/TLS in production deployments; local and development environments may use HTTP.</li>
              <li>File processing uses application workflows, ownership checks, and path separation; deployment-specific controls may vary by environment.</li>
              <li>Redaction removes redacted content from the exported file, not just visually covers it; results can vary by file type, so users must verify final output.</li>
            </ul>
          </section>

          <section className="info-page-section info-page-legal-section">
            <h3>What we’re still building</h3>
            <ul>
              <li>At-rest encryption is not yet in place, and we do not claim encrypted storage for documents or logs today.</li>
              <li>SOC 2, HIPAA, and similar references describe alignment goals or supported-workflow concepts, not current certifications.</li>
              <li>PHI workflows require an executed BAA and a supported deployment configuration before use.</li>
            </ul>
          </section>

          <section className="info-page-section info-page-legal-section">
            <h3>Responsible use</h3>
            <ul>
              <li>Users must verify final output before release or external use.</li>
              <li>AI-assisted detection is probabilistic and may vary by file type, scan quality, and document structure.</li>
              <li>Final review remains required before any file leaves your organization.</li>
            </ul>
          </section>

          <div className="info-page-callout">
            <div className="info-page-callout-title">Need a deeper review?</div>
            <p>Email <a href="mailto:info@redacto.net">info@redacto.net</a>.</p>
          </div>
        </div>
      </div>
      <PublicSiteFooter />
    </div>
  );
}

export default Security;
